import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbExternalLink, TbPencil } from "solid-icons/tb";
import { createMemo, Show } from "solid-js";
import { twMerge } from "tailwind-merge";
import { safeParseBlockJson } from "../message/parseBlockContent";
import { useChatRuntime } from "../runtime/ChatRuntime";
import { type EmailDraftPayload, parseEmailDraftPayload } from "./emailSchema";

interface EmailDraftRendererProps {
  block: StreamingBlock;
  // Drops the card's border and background so it reads as content inside an enclosing group.
  flat?: boolean;
}

// Recipients, subject, and snippet are untrusted and rendered as text only.
export const EmailDraftRenderer = (props: EmailDraftRendererProps) => {
  const rt = useChatRuntime();

  // Surfaces without native anchors (chief's webview) route through the OS browser.
  const handleOpen = (e: MouseEvent, url: string) => {
    if (rt.actions.openExternalUrl) {
      e.preventDefault();
      rt.actions.openExternalUrl(url);
    }
  };

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<EmailDraftPayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseEmailDraftPayload(json);
  });

  const parseFailed = () => !!rawContent() && payload() === null;

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {rt.t("email_draft_card.parse_error")}
        </div>
      }
    >
      <Show when={payload()}>
        {(data) => (
          <div
            class={twMerge(
              "@container max-w-xl rounded-lg border border-primary-border bg-panel p-3",
              props.flat && "max-w-none rounded-none border-0 bg-transparent p-0"
            )}
          >
            <div class="flex items-start gap-3">
              <TbPencil class="size-5 shrink-0 text-brand" />
              <div class="min-w-0 flex-1">
                <h3 class="text-sm font-medium text-primary-text">
                  {data().updated ? rt.t("email_draft_card.updated_title") : rt.t("email_draft_card.title")}
                </h3>

                <div class="mt-2 flex flex-col gap-3 @max-sm:*:self-stretch @sm:flex-row @sm:items-stretch @sm:justify-between">
                  <div class="min-w-0 flex-1 space-y-1 self-start">
                    <p class="truncate text-xs text-muted-text">
                      {rt.t("email_draft_card.to_label")} {data().to.join(", ")}
                    </p>
                    <p class="truncate text-sm font-bold text-primary-text">{data().subject}</p>
                    <p class="mt-1 line-clamp-2 text-xs text-muted-text">{data().snippet}</p>
                  </div>

                  <div class="hidden w-px self-stretch bg-primary-border @sm:block" aria-hidden="true" />

                  <div class="flex shrink-0 flex-col gap-2 self-start @sm:items-end @sm:self-center">
                    <a
                      href={data().webLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => handleOpen(e, data().webLink)}
                      class="flex items-center justify-center gap-2 rounded-md bg-brand px-3 py-1.5 text-sm font-semibold text-on-brand transition-colors hover:bg-brand-hover"
                    >
                      <TbExternalLink class="size-4" />
                      <span>{rt.t("email_draft_card.open_in_gmail")}</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Show>
    </Show>
  );
};
